import type { LucideIcon } from "lucide-react";
import { HandHelping, Target, Users } from "lucide-react";
import { assets } from "./assets";
import { formatCompactCount } from "./footer";

export interface AdminLoginPillar {
  label: string;
  icon: LucideIcon;
}

export interface AdminLoginStat {
  id: string;
  label: string;
  value: number;
  displayValue: string;
}

export const adminLoginContent = {
  tagline: "Bringing Hope, Changing Lives",
  headline: "Kindness Creates Change.",
  subtext:
    "Together, we can build a better tomorrow for those who need it most.",
  image: assets.adminLoginHero,
  imageAlt:
    "Children standing together, looking toward a green landscape — symbol of hope and community",
  formTitle: "Sign in to manage donations",
  formSubtitle: "Secure admin access to your dashboard.",
  trustHeading: "Secure & Trusted",
  trustBody: "Your data is protected with industry-standard security.",
  hopeMessage: "Every act of kindness creates a ripple of hope.",
  pillars: [
    { label: "Support Communities in Need", icon: Users },
    { label: "Empower Lives with Hope", icon: HandHelping },
    { label: "Build a Better Tomorrow", icon: Target },
  ] satisfies AdminLoginPillar[],
} as const;

const adminLoginStatsFallbacks = {
  livesImpacted: 50_000,
  volunteers: 500,
  communities: 50,
} as const;

export function buildAdminLoginStats(
  overrides?: Partial<typeof adminLoginStatsFallbacks>
): AdminLoginStat[] {
  const livesImpacted =
    overrides?.livesImpacted ?? adminLoginStatsFallbacks.livesImpacted;
  const volunteers =
    overrides?.volunteers ?? adminLoginStatsFallbacks.volunteers;
  const communities =
    overrides?.communities ?? adminLoginStatsFallbacks.communities;

  return [
    {
      id: "lives",
      label: "Lives Impacted",
      value: livesImpacted,
      displayValue: formatCompactCount(livesImpacted),
    },
    {
      id: "volunteers",
      label: "Volunteers",
      value: volunteers,
      displayValue: formatCompactCount(volunteers),
    },
    {
      id: "communities",
      label: "Communities",
      value: communities,
      displayValue: formatCompactCount(communities),
    },
  ];
}
